/* Fixed-origin, explicit-rejection challenge flow. Tokens live only in this request. */
(function (root, factory) {
  "use strict";
  const library = factory();
  if (typeof module === "object" && module.exports) module.exports = library;
  else Object.defineProperty(root, "LobbyChallenges", {
    value: Object.freeze(library.createChallengeClient({ window: root, document: root.document, fetch: root.fetch.bind(root) })),
    writable: false, configurable: false,
  });
})(globalThis, function () {
  "use strict";
  const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  const CONFIG_URL = "/api/security/challenge-config";
  const ACTION_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;
  const POST_ROUTES = [
    [/^\/api\/auth\/register$/, "register"],
    [/^\/api\/auth\/login$/, "login"],
    [/^\/api\/auth\/password\/request$/, "password-reset"],
    [/^\/api\/drops$/, "abuse_drop"],
    [/^\/api\/chat\/messages$/, "abuse_chat"],
    [/^\/api\/drops\/[A-Za-z0-9-]+\/comments$/, "abuse_comment"],
    [/^\/api\/messages\/conversations\/[A-Za-z0-9-]+\/messages$/, "abuse_dm"],
    [/^\/api\/messages\/groups$/, "abuse_dm"],
    [/^\/api\/messages\/conversations\/[A-Za-z0-9-]+\/(?:polls|albums|collections|scheduled)$/, "abuse_dm"],
    [/^\/api\/story-items$/, "abuse_drop"],
    [/^\/api\/story-highlights$/, "abuse_profile"],
    [/^\/api\/safety\/reports$/, "abuse_report"],
    [/^\/api\/questions$/, "abuse_question_create"],
    [/^\/api\/questions\/[A-Za-z0-9-]+\/responses$/, "abuse_question_response"],
    [/^\/api\/polls$/, "abuse_poll_create"],
    [/^\/api\/polls\/[A-Za-z0-9-]+\/votes$/, "abuse_poll_vote"],
    [/^\/api\/collaborations$/, "abuse_collaboration_create"],
    [/^\/api\/collaborations\/[A-Za-z0-9-]+\/interests$/, "abuse_collaboration_interest"],
    [/^\/api\/world\/mic\/entries$/, "abuse_mic_entry"],
  ];
  const PATCH_ROUTES = [
    [/^\/api\/messages\/groups\/[A-Za-z0-9-]+$/, "abuse_dm"],
    [/^\/api\/messages\/conversations\/[A-Za-z0-9-]+\/(?:albums|collections)\/[A-Za-z0-9-]+$/, "abuse_dm"],
    [/^\/api\/story-highlights\/[A-Za-z0-9-]+$/, "abuse_profile"],
    [/^\/api\/messages\/conversations\/[A-Za-z0-9-]+\/messages\/[A-Za-z0-9-]+$/, "abuse_dm"],
    [/^\/api\/drops\/[A-Za-z0-9-]+$/, "abuse_drop"],
    [/^\/api\/account\/profile$/, "abuse_profile"],
  ];
  const PUT_ROUTES = [
    [/^\/api\/world\/map\/me$/, "abuse_region_update"],
  ];

  function requestAction(pathname, init) {
    const routes = init.method === "POST"
      ? POST_ROUTES
      : init.method === "PATCH"
        ? PATCH_ROUTES
        : init.method === "PUT"
          ? PUT_ROUTES
          : null;
    if (!routes) return null;
    if (init.method === "POST" && /^\/api\/follows\/[A-Za-z0-9-]+$/.test(pathname)
        && init.body === undefined) {
      return { action: "abuse_follow", transport: "header" };
    }
    if (init.headers.get("Content-Type")?.split(";")[0].trim().toLowerCase() !== "application/json"
        || typeof init.body !== "string" || init.body.length > 1024 * 1024) return null;
    let payload;
    try { payload = JSON.parse(init.body); } catch { return null; }
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
    if (init.method === "POST" && /^\/api\/drops\/[A-Za-z0-9-]+\/signals$/.test(pathname)) {
      const action = { applaud: "abuse_reaction", save: "abuse_save", nominate: "abuse_board_nomination" }[payload.kind];
      return typeof action === "string" ? { action, transport: "json", payload } : null;
    }
    const match = routes.find(([pattern]) => pattern.test(pathname));
    return match ? { action: match[1], transport: "json", payload } : null;
  }

  function createChallengeClient(options) {
    const win = options.window;
    const doc = options.document;
    const nativeFetch = options.fetch;
    const pending = new Map();
    let scriptPromise = null;
    let activeDialog = false;
    let sequence = 0;
    let pageActive = true;
    let pageGeneration = 0;
    // Late timers and challenge completions must not start requests from a departing page.
    // pageshow restores service when the browser returns this document from its back/forward cache.
    win.addEventListener?.("pagehide", () => { pageActive = false; pageGeneration += 1; });
    win.addEventListener?.("pageshow", () => { pageActive = true; });

    async function fetchOnce(path, init) {
      if (!pageActive) throw new DOMException("The page is no longer active.", "AbortError");
      const controller = new AbortController();
      const abort = () => controller.abort(init.signal.reason);
      if (init.signal?.aborted) abort();
      else init.signal?.addEventListener("abort", abort, { once: true });
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, options.networkTimeoutMs ?? 20000);
      let rejectAborted;
      const aborted = new Promise((_, reject) => { rejectAborted = reject; });
      const rejectAbort = () => rejectAborted(controller.signal.reason);
      controller.signal.addEventListener("abort", rejectAbort, { once: true });
      try {
        if (controller.signal.aborted) throw controller.signal.reason;
        const operation = (async () => {
          const response = await nativeFetch(path, { ...init, signal: controller.signal });
          // Include the response body in the deadline, not just receipt of headers.
          const body = await response.arrayBuffer();
          return new Response(init.method === "HEAD" || [204, 205, 304].includes(response.status) ? null : body, {
            status: response.status, statusText: response.statusText, headers: response.headers,
          });
        })();
        return await Promise.race([operation, aborted]);
      } catch (error) {
        if (timedOut) {
          const message = ["GET", "HEAD"].includes(init.method)
            ? "The request timed out. Please try again."
            : "The response timed out. Your draft is kept. Check whether the action completed before trying again.";
          throw Object.assign(new Error(message), { name: "TimeoutError" });
        }
        throw error;
      } finally {
        clearTimeout(timeout);
        init.signal?.removeEventListener("abort", abort);
        controller.signal.removeEventListener("abort", rejectAbort);
      }
    }

    function turnstileApi() {
      const api = win.turnstile;
      return api && typeof api.render === "function" && typeof api.remove === "function"
        && typeof api.reset === "function" ? api : null;
    }

    function loadScript() {
      if (turnstileApi()) return Promise.resolve(turnstileApi());
      if (scriptPromise) return scriptPromise;
      scriptPromise = new Promise((resolve, reject) => {
        const script = doc.createElement("script");
        script.src = SCRIPT_URL;
        script.async = true;
        const timer = win.setTimeout(() => fail(), 12000);
        function fail() {
          win.clearTimeout(timer);
          script.remove();
          reject(new Error("Verification could not load. Your draft is unchanged. Try again later."));
        }
        script.addEventListener("error", fail, { once: true });
        script.addEventListener("load", () => {
          win.clearTimeout(timer);
          const api = turnstileApi();
          if (api) resolve(api); else fail();
        }, { once: true });
        doc.head.append(script);
      }).catch((error) => { scriptPromise = null; throw error; });
      return scriptPromise;
    }

    function showChallenge(action, { signal } = {}) {
      if (activeDialog) return Promise.reject(new Error("Another verification is already open. Your draft is unchanged."));
      if (signal?.aborted) return Promise.reject(new DOMException("Verification cancelled. Your draft is unchanged.", "AbortError"));
      activeDialog = true;
      return new Promise((resolve, reject) => {
        const priorFocus = doc.activeElement;
        const dialog = doc.createElement("dialog");
        const title = doc.createElement("h2");
        const copy = doc.createElement("p");
        const message = doc.createElement("p");
        const container = doc.createElement("div");
        const footer = doc.createElement("footer");
        const cancel = doc.createElement("button");
        const retry = doc.createElement("button");
        const controller = new AbortController();
        let settled = false;
        let api = null;
        let widgetId = null;
        let retriedWidget = false;
        dialog.className = "small-dialog";
        title.id = `lobby-challenge-title-${++sequence}`;
        title.textContent = "Verify you're human";
        dialog.setAttribute("aria-labelledby", title.id);
        copy.textContent = "Complete this check to continue. Your draft will stay unchanged if you cancel.";
        message.textContent = "Loading verification…";
        message.setAttribute("role", "status");
        message.setAttribute("aria-live", "polite");
        cancel.type = retry.type = "button";
        cancel.className = retry.className = "secondary-button";
        cancel.textContent = "Cancel";
        retry.textContent = "Retry verification";
        retry.hidden = true;
        footer.append(cancel, retry);
        dialog.append(title, copy, message, container, footer);

        function finish(error, token) {
          if (settled) return;
          settled = true;
          controller.abort();
          win.clearTimeout(deadline);
          signal?.removeEventListener("abort", abort);
          win.removeEventListener?.("pagehide", abort);
          if (api && widgetId !== null) { try { api.remove(widgetId); } catch { /* Already removed. */ } }
          if (dialog.open) dialog.close();
          dialog.remove();
          activeDialog = false;
          if (pageActive && priorFocus?.isConnected && typeof priorFocus.focus === "function") priorFocus.focus();
          if (error) reject(error); else resolve(token);
        }
        function abort() { finish(new DOMException("Verification cancelled. Your draft is unchanged.", "AbortError")); }
        function recoverableFailure() {
          if (settled) return;
          message.textContent = "Verification expired or failed. Your draft is unchanged.";
          retry.hidden = retriedWidget || widgetId === null;
          if (!retry.hidden) retry.focus(); else cancel.focus();
        }
        const deadline = win.setTimeout(() => finish(new Error("Verification timed out. Your draft is unchanged.")), 180000);
        signal?.addEventListener("abort", abort, { once: true });
        win.addEventListener?.("pagehide", abort, { once: true });
        cancel.addEventListener("click", abort);
        dialog.addEventListener("cancel", (event) => { event.preventDefault(); abort(); });
        retry.addEventListener("click", () => {
          if (settled || retriedWidget || !api || widgetId === null) return;
          retriedWidget = true;
          retry.hidden = true;
          message.textContent = "Complete the new verification check.";
          cancel.focus();
          try { api.reset(widgetId); } catch { recoverableFailure(); }
        });
        doc.body.append(dialog);
        try { dialog.showModal(); cancel.focus(); } catch {
          finish(new Error("This browser cannot show verification. Your draft is unchanged."));
          return;
        }
        (async () => {
          const configTimeout = win.setTimeout(() => controller.abort(), 10000);
          let config;
          try {
            const result = await nativeFetch(CONFIG_URL, { credentials: "same-origin", cache: "no-store", signal: controller.signal });
            if (!result.ok || !result.headers.get("Content-Type")?.toLowerCase().startsWith("application/json")) {
              throw new Error("Verification is unavailable. Your draft is unchanged.");
            }
            const text = await result.text();
            if (text.length > 16384) throw new Error("Invalid verification configuration.");
            config = JSON.parse(text);
          } finally { win.clearTimeout(configTimeout); }
          if (settled) return;
          if (!config || config.enabled !== true || config.provider !== "turnstile"
              || typeof config.site_key !== "string" || !/^[A-Za-z0-9_-]{3,256}$/.test(config.site_key)
              || !Array.isArray(config.actions) || config.actions.length > 32
              || !config.actions.every(value => typeof value === "string" && ACTION_PATTERN.test(value))
              || !config.actions.includes(action)) {
            throw new Error("Verification is unavailable. Your draft is unchanged. Try again later.");
          }
          api = await loadScript();
          if (settled) return;
          message.textContent = "Complete the verification below to continue.";
          widgetId = api.render(container, {
            sitekey: config.site_key, action, size: "compact", theme: "auto",
            "response-field": false, retry: "never", "refresh-expired": "never", "refresh-timeout": "never",
            callback(token) {
              if (typeof token !== "string" || token.length < 1 || token.length > 2048) { recoverableFailure(); return; }
              finish(null, token);
            },
            "error-callback": recoverableFailure, "expired-callback": recoverableFailure,
            "timeout-callback": recoverableFailure, "unsupported-callback": recoverableFailure,
          });
          // A synchronous callback is permitted by test adapters; still remove its widget.
          if (settled) { try { api.remove(widgetId); } catch { /* Already removed. */ } }
        })().catch(() => {
          if (!settled) finish(new Error("Verification is unavailable. Your draft is unchanged. Try again later."));
        });
      });
    }

    const solve = options.solveChallenge || showChallenge;
    async function fetchWithChallenge(path, supplied = {}) {
      const url = new URL(path, win.location.origin);
      if (url.origin !== win.location.origin || url.username || url.password) throw new Error("API requests must be same-origin.");
      const init = { ...supplied, method: (supplied.method || "GET").toUpperCase(), headers: new Headers(supplied.headers || {}) };
      if (!pageActive) throw new DOMException("The page is no longer active.", "AbortError");
      if (init.signal?.aborted) throw init.signal.reason;
      const roomQuery = url.pathname === "/api/chat/messages"
        && url.searchParams.size === 1 && url.searchParams.has("room")
        && ["global", "feedback", "collaborations", "music", "art", "film", "gaming", "tech"].includes(url.searchParams.get("room"));
      const binding = (!url.search || roomQuery) && !url.hash ? requestAction(url.pathname, init) : null;
      if (!binding) return fetchOnce(path, init);
      const key = JSON.stringify([init.method, url.pathname, url.search, init.body,
        init.headers.get("X-CSRF-Token"), init.headers.get("X-Account-ID")]);
      if (!pending.has(key)) {
        if (pending.size >= 8) throw new Error("Too many requests are pending. Your draft is unchanged.");
        const generation = pageGeneration;
        const checkPage = () => {
          if (!pageActive || generation !== pageGeneration) {
            throw new DOMException("Verification cancelled. Your draft is unchanged.", "AbortError");
          }
        };
        const operation = (async () => {
          const result = await fetchOnce(path, init);
          // This header is emitted before a domain write. Other errors are never replayed.
          if (result.status !== 403 || result.headers.get("X-Challenge-Required") !== binding.action) return result;
          checkPage();
          const token = await solve(binding.action, { signal: init.signal });
          checkPage();
          if (init.signal?.aborted) throw new DOMException("Verification cancelled. Your draft is unchanged.", "AbortError");
          if (typeof token !== "string" || !token || token.length > 2048) throw new Error("Verification failed. Your draft is unchanged.");
          const retry = { ...init, headers: new Headers(init.headers) };
          if (binding.transport === "header") retry.headers.set("X-Challenge-Token", token);
          else retry.body = JSON.stringify({ ...binding.payload, challenge_token: token });
          // At most ONE resubmit. A second rejection or ambiguous network error surfaces to the caller.
          return fetchOnce(path, retry);
        })().finally(() => pending.delete(key));
        pending.set(key, operation);
      }
      return (await pending.get(key)).clone();
    }
    return { fetch: fetchWithChallenge };
  }
  return { createChallengeClient };
});
